import { useMemo, useState, useEffect } from 'react';
import throttle from 'lodash.throttle';
export const DEFAULT_WAIT_TIME = 100;
const getScrollPosition = (element)=>{
    if (!element) {
        return {
            x: 0,
            y: 0
        };
    }
    if (element instanceof Window) {
        return {
            x: window.scrollX,
            y: window.scrollY
        };
    }
    return {
        x: element.scrollLeft,
        y: element.scrollTop
    };
};
/**
 * A custom hook to track the scroll position of an element or the window.
 *
 * @param {HTMLElement | Window | null} [element=null] - The target element to track its scroll position. If not provided, the window scroll position will be tracked.
 * @param {number} [waitTime=DEFAULT_WAIT_TIME] - The wait time (in milliseconds) for throttling the scroll event. Default value is set by the DEFAULT_WAIT_TIME constant.
 * @returns {ScrollPosition} - The current scroll position of the target element or the window.
 *
 * @example
 * const scrollPosition = useScrolling(elementRef.current, 100);
 */ export const useScrolling = (element = null, waitTime = DEFAULT_WAIT_TIME)=>{
    const [scrollPosition, setScrollPosition] = useState(()=>getScrollPosition(element));
    const throttledOnScroll = useMemo(()=>throttle(()=>{
            const position = getScrollPosition(element);
            setScrollPosition(position);
        }, waitTime, {
            leading: true,
            trailing: true
        }), [
        waitTime,
        element
    ]);
    useEffect(()=>{
        element?.addEventListener('scroll', throttledOnScroll);
        return ()=>{
            element?.removeEventListener('scroll', throttledOnScroll);
            throttledOnScroll.cancel();
        };
    }, [
        element,
        throttledOnScroll
    ]);
    return scrollPosition;
};

//# sourceMappingURL=useScrolling.js.map